// assets/custom-line-item-properties.js

class CustomLineItemProperties {
  constructor(root = document) {
    this.root = root;
  }

  init() {
    this.#bindFieldsetSync();
    this.#bindFormValidation();
    this.#bindLogoCustomizationToggle();
    this.#bindBusinessCardPreview();
    this.#checkPDFButtonVisibility();
    this.#ensureOptionalEmail();
  }

  #bindFieldsetSync() {
    const fieldsets = this.root.querySelectorAll('fieldset[data-prop]');
    fieldsets.forEach((fieldset) => {
      // Initialize current value on load
      this.#syncRadioToHidden(fieldset);

      fieldset.addEventListener('change', (event) => {
        if (event.target && event.target.matches('input[type="radio"]')) {
          this.#syncRadioToHidden(fieldset);
        }
      });
    });
  }

  #syncRadioToHidden(fieldset) {
    const propType = fieldset.getAttribute('data-prop');
    if (!propType) return;

    const hiddenSelector = propType === 'location_options' ? '[data-location]' : '[data-style]';
    const hiddenInput = document.querySelector(`input${hiddenSelector}`);
    if (!hiddenInput) return;

    const selected = fieldset.querySelector('input[type="radio"]:checked');
    if (selected) hiddenInput.value = selected.value;
  }

  #bindFormValidation() {
    // Use capture-phase to ensure we catch submits even if components handle them
    this.root.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (form.getAttribute('data-type') !== 'add-to-cart-form') return;

        // Check HTML5 form validity first
        if (!form.checkValidity()) {
          // Let browser handle native validation messages
          return;
        }

        // Then check custom required options
        if (!this.#validateRequiredOptions(form)) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );
  }

  

  #validateRequiredOptions(form) {
    const productFormComponent = form.closest('product-form-component');
    const sectionId = productFormComponent?.dataset.sectionId;

    // Check if this is a business card product (has business card fields)
    const isBusinessCard = form.querySelector('#business-name') !== null;

    if (isBusinessCard) {
      // Check if proof approval is required - must be selected to add to cart
      const proofApproval = form.querySelector('#proof-approval');

      if (!proofApproval || proofApproval.value.trim() === '') {
        alert('⚠️ Proof Approval Required\n\nPlease review the business card PDF and select your approval status before adding this item to your cart.');
        proofApproval.focus();
        return false;
      }

      // Proof approval selected - allow cart addition
      return true;
    }

    // Prefer validating directly from radios in the related variant-picker
    let styleSelected = false;
    let locationSelected = false;

    if (sectionId) {
      const variantPicker = document.querySelector(`variant-picker[data-section-id="${sectionId}"]`);
      if (variantPicker) {
        const styleFs = variantPicker.querySelector('fieldset[data-prop="style_options"]');
        const locationFs = variantPicker.querySelector('fieldset[data-prop="location_options"]');
        styleSelected = !!styleFs?.querySelector('input[type="radio"]:checked');
        locationSelected = !!locationFs?.querySelector('input[type="radio"]:checked');
      }
    }

    // Fallback to hidden inputs if radios not found for any reason
    if (!styleSelected || !locationSelected) {
      const formId = form.getAttribute('id') || '';
      const styleInput = document.querySelector(`input[data-style][form="${formId}"]`);
      const locationInput = document.querySelector(`input[data-location][form="${formId}"]`);
      styleSelected = styleSelected || (!!styleInput && !!styleInput.value);
      locationSelected = locationSelected || (!!locationInput && !!locationInput.value);
    }

    const missing = [];
    if (!styleSelected) missing.push('style');
    if (!locationSelected) missing.push('location');

    
    return true;
  }

  #bindLogoCustomizationToggle() {
    const logoInput = this.root.querySelector('#logo-customization-input');
    const fileUploadWrapper = this.root.querySelector('#file-upload-wrapper');
    
    if (!logoInput || !fileUploadWrapper || !(logoInput instanceof HTMLInputElement)) return;

    const toggleFileUpload = () => {
      if (logoInput.value.trim() !== '') {
        fileUploadWrapper.classList.remove('d-none');
      } else {
        fileUploadWrapper.classList.add('d-none');
      }
    };

    // Check initial value
    toggleFileUpload();

    // Listen for input changes
    logoInput.addEventListener('input', toggleFileUpload);
    logoInput.addEventListener('change', toggleFileUpload);
  }

  // Ensure business email is never treated as a required HTML field
  #ensureOptionalEmail() {
    const emailInput = this.root.querySelector('input[name="properties[Email]"]');
    if (emailInput instanceof HTMLInputElement) {
      emailInput.removeAttribute('required');
      emailInput.required = false;
    }
  }

  // Check PDF button visibility based on form completion
  #checkPDFButtonVisibility() {
    const preview = document.querySelector("[data-business-card-preview]");
    if (!preview) return;

    const name = preview.querySelector("[data-preview-name]")?.textContent.trim();
    const title = preview.querySelector("[data-preview-title]")?.textContent.trim();
    const localization1 = preview.querySelector("[data-preview-localization-1]")?.textContent.trim();
    const localization2 = preview.querySelector("[data-preview-localization-2]")?.textContent.trim();
    const addr1 = preview.querySelector("[data-preview-address-1]")?.textContent.trim();
    const addr2 = preview.querySelector("[data-preview-address-2]")?.textContent.trim();
    const addr3 = preview.querySelector("[data-preview-address-3]")?.textContent.trim();
    const addr4 = preview.querySelector("[data-preview-address-4]")?.textContent.trim();
    const officePhone = preview.querySelector("[data-preview-office-phone]")?.textContent.trim();
    const mobilePhone = preview.querySelector("[data-preview-mobile-phone]")?.textContent.trim();
    const email = preview.querySelector("[data-preview-email]")?.textContent.trim();
    const website = preview.querySelector("[data-preview-website]")?.textContent.trim();

    // Check if fields contain actual user input (not placeholder text)
    const isNameFilled = name && name !== "First and Last Name";
    const isTitleFilled = title && title !== "Title Here";
    const isLocalization1Filled = localization1 && localization1 !== "Localization 1";
    const isLocalization2Filled = localization2 && localization2 !== "Localization 2";
    const isAddr1Filled = addr1 && addr1 !== "Address Line 1";
    const isAddr2Filled = addr2 && addr2 !== "Address Line 2";
    const isAddr3Filled = addr3 && addr3 !== "Address Line 3";
    const isAddr4Filled = addr4 && addr4 !== "Address Line 4";
    const isOfficePhoneFilled = officePhone && officePhone !== "Office Phone";
    const isMobilePhoneFilled = mobilePhone && mobilePhone !== "Mobile Phone";
    const isEmailFilled = email && email !== "Email";
    const isWebsiteFilled = website && website !== "Website";

    const hasAllFields = isNameFilled && isTitleFilled && isLocalization1Filled && isLocalization2Filled &&
                        isAddr1Filled && isAddr2Filled && isAddr3Filled && isAddr4Filled &&
                        isOfficePhoneFilled && isMobilePhoneFilled && isEmailFilled && isWebsiteFilled;

    // Show/hide proof approval container based on all fields being filled
    const proofApprovalContainer = document.getElementById("proof-approval-container");
  }

  #bindBusinessCardPreview() {
    const businessCard = this.root.querySelector('[data-business-card-preview]');
    if (!businessCard) return;

    const nameEl = businessCard.querySelector('[data-preview-name]');
    const titleEl = businessCard.querySelector('[data-preview-title]');
    const localization1El = businessCard.querySelector('[data-preview-localization-1]');
    const localization2El = businessCard.querySelector('[data-preview-localization-2]');
    const address1El = businessCard.querySelector('[data-preview-address-1]');
    const address2El = businessCard.querySelector('[data-preview-address-2]');
    const address3El = businessCard.querySelector('[data-preview-address-3]');
    const address4El = businessCard.querySelector('[data-preview-address-4]');
    const officePhoneEl = businessCard.querySelector('[data-preview-office-phone]');
    const mobilePhoneEl = businessCard.querySelector('[data-preview-mobile-phone]');
    const emailEl = businessCard.querySelector('[data-preview-email]');
    const websiteEl = businessCard.querySelector('[data-preview-website]');

    // Function to check if product title contains "orange"
    const checkOrangeTitle = () => {
      const productTitle = document.querySelector('.product__title')?.textContent?.trim() || '';
      return productTitle.toLowerCase().includes('orange');
    };

    // Function to update orange styling
    const updateOrangeStyling = () => {
      const hasOrangeTitle = checkOrangeTitle();

      if (nameEl) {
        // Get the current name value from the input field
        const nameInput = document.querySelector('input[name="properties[Name]"]');
        const currentNameValue = nameInput && nameInput.value.trim() ? nameInput.value.trim() : '';

        if (hasOrangeTitle && currentNameValue) {
          // Update inner text content when product title contains "orange"
          nameEl.textContent = currentNameValue + ' (Orange Special)';
        } else if (currentNameValue) {
          // Show just the current name value when not an orange product
          nameEl.textContent = currentNameValue;
        } else {
          // No current input value: leave existing inner text as-is so that
          // the initial Liquid / HTML placeholder is not cleared on page load.
        }
      }

      if (websiteEl) {
        // Get the current website value from the input field
        const websiteInput = document.querySelector('input[name="properties[Website]"]');
        const currentWebsiteValue = websiteInput && websiteInput.value.trim() ? websiteInput.value.trim() : '';

        if (hasOrangeTitle && currentWebsiteValue) {
          // Update inner text content when product title contains "orange"
          websiteEl.textContent = currentWebsiteValue + ' (Orange Theme)';
        } else if (currentWebsiteValue) {
          // Show just the current website value when not an orange product
          websiteEl.textContent = currentWebsiteValue;
        } else {
          // No current input value: keep whatever placeholder text is already
          // rendered in the preview element (don't blank it on load).
        }
      }
    };


    if (!nameEl || !titleEl || !localization1El || !emailEl || !officePhoneEl) return;

    const updatePreviewValue = (inputName, element, isLink = false, isPhone = false, phonePrefix = '') => {
      const input = this.root.querySelector(`input[name="${inputName}"], textarea[name="${inputName}"]`);
      if (!input || !element || !(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)) return false;

      const value = input.value.trim();

      if (value != '') {
        if (isPhone && phonePrefix) {
          element.textContent = phonePrefix + '    ' + value;
        } else if (isLink) {
          element.textContent = value;
        } else {
          element.textContent = value;
        }
        element.style.display = '';
      } else {
        element.innerHTML = '';
        element.textContent = '';
      }
      return true;
    };

    const updateAllPreviews = () => {
      updatePreviewValue('properties[Name]', nameEl);
      updatePreviewValue('properties[Title]', titleEl);
      updatePreviewValue('properties[Localization 1]', localization1El);
      updatePreviewValue('properties[Localization 2]', localization2El);
      updatePreviewValue('properties[Address line 1]', address1El);
      updatePreviewValue('properties[Address line 2]', address2El);
      updatePreviewValue('properties[Address line 3]', address3El);
      updatePreviewValue('properties[Address line 4]', address4El);
      // Direct phone updates using intl-tel-input
      const rawOfficePhone = (window.officePhoneITI && window.officePhoneITI.getNumber()) ;
      const rawMobilePhone = (window.mobilePhoneITI && window.mobilePhoneITI.getNumber()) ;

      // Only update if there's a valid phone number, otherwise keep placeholder text
      if (officePhoneEl && window.officePhoneITI && rawOfficePhone && rawOfficePhone.trim()) {
        const officePhone = this.#formatPhoneWithCountryCode(rawOfficePhone, window.officePhoneITI);
        officePhoneEl.textContent = 'O    ' + officePhone;
      }
      if (mobilePhoneEl && window.mobilePhoneITI && rawMobilePhone && rawMobilePhone.trim()) {
        const mobilePhone = this.#formatPhoneWithCountryCode(rawMobilePhone, window.mobilePhoneITI);
        mobilePhoneEl.textContent = 'M ' + mobilePhone;
      }
      updatePreviewValue('properties[Email]', emailEl, true);
      updatePreviewValue('properties[Website]', websiteEl, true);

      // Update orange styling
      updateOrangeStyling();
    };

    // Direct phone number update function using intl-tel-input
    const updatePhoneDisplay = () => {
      const rawOfficePhone = (window.officePhoneITI && window.officePhoneITI.getNumber()) ;
      const rawMobilePhone = (window.mobilePhoneITI && window.mobilePhoneITI.getNumber()) ;

      // Only update if there's a valid phone number, otherwise keep placeholder text
      if (officePhoneEl && window.officePhoneITI && rawOfficePhone && rawOfficePhone.trim()) {
        const officePhone = this.#formatPhoneWithCountryCode(rawOfficePhone, window.officePhoneITI);
        officePhoneEl.textContent = 'O    ' + officePhone;
        let officePhoneInput = this.root.querySelector('#business-office-phone');
        officePhoneInput.value = officePhone;
      }

      if (mobilePhoneEl && window.mobilePhoneITI && rawMobilePhone && rawMobilePhone.trim()) {
        const mobilePhone = this.#formatPhoneWithCountryCode(rawMobilePhone, window.mobilePhoneITI);
        mobilePhoneEl.textContent = 'M ' + mobilePhone;
      }
    };

    // Update phones immediately and after a delay to ensure form is loaded
    updatePhoneDisplay();
    setTimeout(updatePhoneDisplay, 500);
    setTimeout(updatePhoneDisplay, 1000);

    // Use event delegation on document to catch all input events
    const handleInput = (event) => {
      const target = event.target;
      if (target && target instanceof HTMLElement && target.matches && target.matches('input[name="properties[Name]"], input[name="properties[Title]"], input[name="properties[Localization 1]"], input[name="properties[Localization 2]"], input[name="properties[Address line 1]"], input[name="properties[Address line 2]"], input[name="properties[Address line 3]"], input[name="properties[Address line 4]"], input[name="properties[Office Phone]"], input[name="properties[Mobile phone]"], input[name="properties[Email]"], input[name="properties[Website]"]')) {
        const name = target.getAttribute('name');
        if (name === 'properties[Name]') {
          updatePreviewValue('properties[Name]', nameEl);
        } else if (name === 'properties[Title]') {
          updatePreviewValue('properties[Title]', titleEl);
        } else if (name === 'properties[Localization 1]') {
          updatePreviewValue('properties[Localization 1]', localization1El);
        } else if (name === 'properties[Localization 2]') {
          updatePreviewValue('properties[Localization 2]', localization2El);
        } else if (name === 'properties[Address line 1]') {
          updatePreviewValue('properties[Address line 1]', address1El);
        } else if (name === 'properties[Address line 2]') {
          updatePreviewValue('properties[Address line 2]', address2El);
        } else if (name === 'properties[Address line 3]') {
          updatePreviewValue('properties[Address line 3]', address3El);
        } else if (name === 'properties[Address line 4]') {
          updatePreviewValue('properties[Address line 4]', address4El);
        } else if (name === 'properties[Office Phone]') {
          // Update office phone display using intl-tel-input formatted number
          const businessCard = document.querySelector('[data-business-card-preview]');
          const officePhoneEl = businessCard?.querySelector('[data-preview-office-phone]');
          if (officePhoneEl ) {
            const rawNumber = window.officePhoneITI.getNumber();
            if (rawNumber) {
              const formattedNumber = this.#formatPhoneWithCountryCode(rawNumber, window.officePhoneITI);
              officePhoneEl.textContent = 'O    ' + formattedNumber;
              let officePhoneInput = this.root.querySelector('#business-office-phone');
              officePhoneInput.value = formattedNumber;

            }
          }
        } else if (name === 'properties[Mobile phone]') {
          // Directly update mobile phone display
          const businessCard = document.querySelector('[data-business-card-preview]');
          const mobilePhoneEl = businessCard?.querySelector('[data-preview-mobile-phone]');

          if (mobilePhoneEl && window.mobilePhoneITI && typeof window.mobilePhoneITI.getNumber === 'function') {
            const rawNumber = window.mobilePhoneITI.getNumber();
            if (rawNumber) {
              const formattedNumber = this.#formatPhoneWithCountryCode(rawNumber, window.mobilePhoneITI);
              mobilePhoneEl.textContent = 'M ' + formattedNumber;
            }
          }
        } else if (name === 'properties[Email]') {
          updatePreviewValue('properties[Email]', emailEl, true);
        } else if (name === 'properties[Website]') {
          updatePreviewValue('properties[Website]', websiteEl, true);
        }
      }

      // Update orange styling for all inputs
      updateOrangeStyling();

      // Check PDF button visibility
      this.#checkPDFButtonVisibility();
    };

    const handleChange = (event) => {
      const target = event.target;
      if (target && target instanceof HTMLElement && target.matches && target.matches('input[name="properties[Name]"], input[name="properties[Title]"], input[name="properties[Localization 1]"], input[name="properties[Localization 2]"], input[name="properties[Address line 1]"], input[name="properties[Address line 2]"], input[name="properties[Address line 3]"], input[name="properties[Address line 4]"], input[name="properties[Office Phone]"], input[name="properties[Mobile phone]"], input[name="properties[Email]"], input[name="properties[Website]"]')) {
        const name = target.getAttribute('name');
        if (name === 'properties[Name]') {
          updatePreviewValue('properties[Name]', nameEl);
        } else if (name === 'properties[Title]') {
          updatePreviewValue('properties[Title]', titleEl);
        } else if (name === 'properties[Localization 1]') {
            updatePreviewValue('properties[Localization 1]', localization1El);
        } else if (name === 'properties[Email]') {
          updatePreviewValue('properties[Email]', emailEl, true);
        } else if (name === 'properties[Office Phone]') {
          // Office phone is handled by specific phone input listeners, skip generic handler
          return;
        }
      }
    };

    // Add event listeners
    this.root.querySelector('.business-card-accordion').addEventListener('input', handleInput);
    this.root.querySelector('.business-card-accordion').addEventListener('change', handleChange);

    // Add specific phone input event listeners for live preview updates
    const officePhoneInput = this.root.querySelector('#business-office-phone');
    const mobilePhoneInput = this.root.querySelector('#business-mobile-phone');

    if (officePhoneInput) {
      officePhoneInput.addEventListener('input', () => {
        setTimeout(() => {
          const rawOfficePhone = (window.officePhoneITI && window.officePhoneITI.getNumber()) || officePhoneInput.value.trim() ;
          if (officePhoneEl && window.officePhoneITI && rawOfficePhone && rawOfficePhone.trim()) {
            const officePhone = this.#formatPhoneWithCountryCode(rawOfficePhone, window.officePhoneITI);
            officePhoneEl.textContent = 'O    ' + officePhone;
          } else if (officePhoneEl && (!rawOfficePhone || !rawOfficePhone.trim())) {
            // Reset to placeholder if input is cleared
            officePhoneEl.textContent = '';
          }
          this.#checkPDFButtonVisibility();
        }, 100);
      });

      // Add blur event listener to ensure preview updates when focus leaves the input
      officePhoneInput.addEventListener('blur', () => {
        setTimeout(() => {
          const rawOfficePhone = (window.officePhoneITI && window.officePhoneITI.getNumber()) || officePhoneInput.value.trim() ;
          if (officePhoneEl && window.officePhoneITI && rawOfficePhone && rawOfficePhone.trim()) {
            const officePhone = this.#formatPhoneWithCountryCode(rawOfficePhone, window.officePhoneITI);
            officePhoneEl.textContent = 'O    ' + officePhone;
            let officePhoneInput = this.root.querySelector('#business-office-phone');
            officePhoneInput.value = officePhone;
          } else if (officePhoneEl && (!rawOfficePhone || !rawOfficePhone.trim())) {
            // Reset to placeholder if input is cleared
            officePhoneEl.textContent = '';
            let officePhoneInput = this.root.querySelector('#business-office-phone');
            officePhoneInput.value = officePhoneInput.value;
          }
          this.#checkPDFButtonVisibility();
        }, 100);
      });

      // Listen for country changes
      if (window.officePhoneITI) {
        officePhoneInput.addEventListener('countrychange', () => {
          setTimeout(() => {
            const rawOfficePhone = (window.officePhoneITI && window.officePhoneITI.getNumber()) || officePhoneInput.value.trim() ;
            if (officePhoneEl && window.officePhoneITI && rawOfficePhone && rawOfficePhone.trim()) {
              const officePhone = this.#formatPhoneWithCountryCode(rawOfficePhone, window.officePhoneITI);
              let officePhoneInput = this.root.querySelector('#business-office-phone');
              officePhoneInput.value = officePhone;
              officePhoneEl.textContent = 'O    ' + officePhone;
            }
            this.#checkPDFButtonVisibility();
          }, 100);
        });
      }
    }

    if (mobilePhoneInput) {
      mobilePhoneInput.addEventListener('input', () => {
        setTimeout(() => {
          const rawMobilePhone = (window.mobilePhoneITI && window.mobilePhoneITI.getNumber()) || mobilePhoneInput.value.trim() ;
          if (mobilePhoneEl && window.mobilePhoneITI && rawMobilePhone && rawMobilePhone.trim()) {
            const mobilePhone = this.#formatPhoneWithCountryCode(rawMobilePhone, window.mobilePhoneITI);
            mobilePhoneEl.textContent = 'M ' + mobilePhone;
          } else if (mobilePhoneEl && (!rawMobilePhone || !rawMobilePhone.trim())) {
            // Reset to placeholder if input is cleared
            let mobilePhoneInput = this.root.querySelector('#business-mobile-phone');
            mobilePhoneInput.value = mobilePhoneInput.value;
            mobilePhoneEl.textContent = '';
          }
          this.#checkPDFButtonVisibility();
        }, 100);
      });

      // Add blur event listener to ensure preview updates when focus leaves the input
      mobilePhoneInput.addEventListener('blur', () => {
        setTimeout(() => {
          const rawMobilePhone = (window.mobilePhoneITI && window.mobilePhoneITI.getNumber()) || mobilePhoneInput.value.trim() ;
          if (mobilePhoneEl && window.mobilePhoneITI && rawMobilePhone && rawMobilePhone.trim()) {
            const mobilePhone = this.#formatPhoneWithCountryCode(rawMobilePhone, window.mobilePhoneITI);
            mobilePhoneEl.textContent = 'M ' + mobilePhone;
            let mobilePhoneInput = this.root.querySelector('#business-mobile-phone');
            mobilePhoneInput.value = mobilePhone;
          } else if (mobilePhoneEl && (!rawMobilePhone || !rawMobilePhone.trim())) {
            // Reset to placeholder if input is cleared
            mobilePhoneEl.textContent = '';
            let mobilePhoneInput = this.root.querySelector('#business-mobile-phone');
            mobilePhoneInput.value = mobilePhoneInput.value;
          }
          this.#checkPDFButtonVisibility();
        }, 100);
      });

      // Listen for country changes
      if (window.mobilePhoneITI) {
        mobilePhoneInput.addEventListener('countrychange', () => {
          setTimeout(() => {
            const rawMobilePhone = (window.mobilePhoneITI && window.mobilePhoneITI.getNumber()) || mobilePhoneInput.value.trim() ;
            if (mobilePhoneEl && window.mobilePhoneITI && rawMobilePhone && rawMobilePhone.trim()) {
              const mobilePhone = this.#formatPhoneWithCountryCode(rawMobilePhone, window.mobilePhoneITI);
              mobilePhoneEl.textContent = 'M ' + mobilePhone;
              let mobilePhoneInput = this.root.querySelector('#business-mobile-phone');
              mobilePhoneInput.value = mobilePhone;
            }
            this.#checkPDFButtonVisibility();
          }, 100);
        });
      }
    }

    // Initial phone preview update - only update if there are valid phone numbers
    setTimeout(() => {
      const rawOfficePhone = (window.officePhoneITI && window.officePhoneITI.getNumber()) ;
      const rawMobilePhone = (window.mobilePhoneITI && window.mobilePhoneITI.getNumber()) ;

      if (officePhoneEl && window.officePhoneITI && rawOfficePhone && rawOfficePhone.trim()) {
        const officePhone = this.#formatPhoneWithCountryCode(rawOfficePhone, window.officePhoneITI);
        officePhoneEl.textContent = 'O    ' + officePhone;
        let officePhoneInput = this.root.querySelector('#business-office-phone');
        officePhoneInput.value = officePhone;
      }
      if (mobilePhoneEl && window.mobilePhoneITI && rawMobilePhone && rawMobilePhone.trim()) {
        const mobilePhone = this.#formatPhoneWithCountryCode(rawMobilePhone, window.mobilePhoneITI);
        mobilePhoneEl.textContent = 'M ' + mobilePhone;
      }
    }, 1500); // Delay to ensure intl-tel-input is fully initialized

    // Update orange styling for all inputs
    updateOrangeStyling();

  }

  #bindPhoneValidation() {
    // Validate both office phone (required) and mobile phone (optional)
    const phoneInputs = [
      this.root.querySelector('#business-office-phone'),
      this.root.querySelector('#business-mobile-phone')
    ].filter(input => input !== null);

    phoneInputs.forEach(phoneInput => {
      if (!(phoneInput instanceof HTMLInputElement)) return;

      const validatePhone = (phone) => {
        // Remove common formatting characters
        const cleaned = phone.replace(/[\s\-\(\)\.]/g, '');

        // Check if empty (allowed for optional mobile phone)
        if (!cleaned) {
          return { valid: true, message: '' };
        }

        // Check if starts with + and has country code
        if (cleaned.startsWith('+')) {
          // International format: + followed by 1-15 digits
          if (/^\+[1-9]\d{0,14}$/.test(cleaned)) {
            return { valid: true, message: '' };
          }
          return { valid: false, message: 'Phone number must start with + followed by country code and number (e.g., +1234567890)' };
        }

        // Check if it's a valid number without + (10-15 digits)
        if (/^[1-9]\d{9,14}$/.test(cleaned)) {
          return { valid: true, message: '' };
        }

        return { valid: false, message: 'Please enter a valid phone number (10-15 digits, or start with + for international)' };
      };

      // Add input event listener for real-time validation
      phoneInput.addEventListener('input', (event) => {
        const value = phoneInput.value;
        const validation = validatePhone(value);

        if (validation.valid) {
          phoneInput.setCustomValidity('');
        } else {
          phoneInput.setCustomValidity(validation.message);
        }
      });

      // Add blur event listener for final validation
      phoneInput.addEventListener('blur', (event) => {
        const value = phoneInput.value;
        const validation = validatePhone(value);

        if (!validation.valid) {
          phoneInput.reportValidity();
        }
      });
    });
  }

  #formatPhoneWithCountryCode(fullNumber, itiInstance) {
    if (!fullNumber || !itiInstance) return fullNumber;

    // Extract and store country code using itiInstance first (most reliable)
    let countryCode = '';
    try {
      const countryData = itiInstance.getSelectedCountryData();
      if (countryData && countryData.dialCode) {
        countryCode = '+' + countryData.dialCode;
      }
    } catch (error) {
      // Fallback to DOM query if itiInstance method fails
      try {
        // Scope the query to this specific input's container to avoid conflicts
        const inputElement = itiInstance.telInput;
        const itiContainer = inputElement.closest('.iti');
        if (itiContainer) {
          let itiElement = itiContainer.querySelector(".iti__selected-country");
          if (itiElement && itiElement.title) {
            // Example title value: "India: +91" or "United States: +1"
            let countryCodeMatch = itiElement.title.match(/\+\d+/);
            countryCode = countryCodeMatch ? countryCodeMatch[0] : '';
          }
        }
      } catch (domError) {
        // Both itiInstance and DOM methods failed
      }
    }

    if (!countryCode) return fullNumber;

    let localNumber = fullNumber;
    if (fullNumber.startsWith(countryCode)) {
      localNumber = fullNumber.substring(countryCode.length);
    }

    localNumber = localNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    localNumber = localNumber.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');

    return countryCode + ' ' + localNumber.trim();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const app = new CustomLineItemProperties(document);
    app.init();
  });
} else {
  const app = new CustomLineItemProperties(document);
  app.init();
}
